'use client';

import React, { useMemo } from 'react';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useSite } from 'src/context/providers/SiteProvider';
import { ActivityItem } from '../ActivityItem';
import {
  useGetEntityActivitiesQuery,
  useDeleteActivityMutation,
} from 'src/api/redux';
import { AddEditActivity } from '../AddEditActivity';

interface ActivityListProps {
  showLoading?: boolean;
  isFiltered?: boolean;
  activityTypeFilter?: string | null;
}

const ActivityList = ({
  showLoading = false,
  isFiltered = false,
  activityTypeFilter = null,
}: ActivityListProps) => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const { entityDetails } = useProfile();
  const { state } = useSite();

  const provider_id = entityDetails?.id;
  const activityTypeFilters = activityTypeFilter
    ? [activityTypeFilter]
    : state?.profile?.queryParams?.activities;

  const { data: activities, isLoading } = useGetEntityActivitiesQuery(
    {
      providerId: provider_id,
      activityTypes: activityTypeFilters,
    },
    { skip: !provider_id }
  );

  const [deleteActivityMutation] = useDeleteActivityMutation();

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this activity?')) return;
    try {
      await deleteActivityMutation({ activityId: id }).unwrap();

      log.event('Delete Activity', {
        location: 'Activity List',
        data: id,
      });
    } catch (err) {
      log.exception('Delete Activity', {
        tags: {
          location: 'Activity List',
          data: id,
          error: err,
        },
      });
      setNotification({
        title: 'Error',
        message: err?.error_message || 'Failed to delete activity',
        type: 'error',
      });
    }
  };

  const activityItems = useMemo(() => {
    if (!activities?.results) return [];
    return activities?.results;
  }, [activities]);

  return (
    <div className="relative flex w-full" data-testid="activity-list">
      <div className="absolute w-0.5 h-full bg-neutral-200 left-6 top-px" />
      <div
        className="relative flex px-2 flex-col items-start self-stretch gap-6 z-10 w-full"
        role="list"
        aria-label="Activities"
      >
        {isLoading && showLoading ? (
          <div
            className="flex items-center justify-center w-full h-full min-h-40"
            role="status"
            aria-label="Loading activities"
            data-testid="activity-list-loading"
          >
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" />
          </div>
        ) : activityItems?.length > 0 ? (
          activityItems?.map((item) => (
            <ActivityItem key={item.id} item={item} onDelete={handleDelete} />
          ))
        ) : (
          !isLoading &&
          activityItems?.length === 0 && (
            <>
              {!isFiltered && <AddEditActivity />}
              <div
                className="flex items-center gap-4 self-stretch pl-8"
                data-testid="activity-list-empty"
              >
                <h3 className="text-base font-semibold">No activities</h3>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default ActivityList;
